import { IconName } from '@lyse-as/formds-icons';
import { Build, Component, Event, EventEmitter, h, Host, Listen, Method, Prop, State } from '@stencil/core';

import state from '@/store';

export type FdsAlertVariant = 'info' | 'warning' | 'error' | 'success';

/**
 * @slot alert-heading - This slot is for adding the heading. It has to be an h2..h6 element
 * @slot alert-link - This slot is used to insert a link within the alert. Please use the <FdsLink> component within this slot
 * @slot alert-button - This slot is used to insert a tertiary button
 */
@Component({
  tag: 'fds-alert',
  styleUrl: 'styles/alert.scss',
  shadow: true,
})
export class Alert {
  private alertHeading!: HTMLSlotElement;
  private alertLink!: HTMLSlotElement;
  private alertBtn!: HTMLSlotElement;

  /**
   * The variant of alert to display
   */
  @Prop() variant!: FdsAlertVariant;

  /**
   * You can disable the close button to use the component in a different context, for instance in a documentation page to highlight important info
   */
  @Prop() disableDismiss = false;

  /**
   * Hide the alert by default
   */
  @Prop() defaultHidden = false;

  @State() show = !this.defaultHidden;

  /**
   * Emitted when the alert is dismissed
   */
  @Event() fdsDismiss: EventEmitter<void>;

  /**
   * Emitted when the alert is dismissed
   */
  @Event() fdsAlertDismiss: EventEmitter<void>;

  /**
   * Emitted when the button component passed within the alert ic clicked
   */
  @Event() fdsAlertButtonClick: EventEmitter<void>;

  @Listen('click')
  slotsClicked(event: CustomEvent) {
    const slotElement = event.target as HTMLElement;

    if (slotElement.tagName === 'FDS-BUTTON') {
      this.fdsAlertButtonClick.emit();
    }
  }

  componentDidLoad() {
    if (Build.isBrowser) {
      const alertHeading = this.alertHeading.assignedNodes()[0] as HTMLHeadingElement;
      const alertLink = this.alertLink.assignedNodes()[0] as HTMLFdsLinkElement;
      const alertBtn = this.alertBtn.assignedNodes()[0] as HTMLFdsButtonElement;

      if (alertHeading) {
        this.addHeadingClass(alertHeading);
      } else {
        console.warn('fds-alert should contain a heading');
      }

      if (alertLink && alertLink.tagName !== 'FDS-LINK') {
        throw new Error('fds-alert can only accept a fds-link in the alert-link slot');
      }

      if (alertBtn) {
        if (alertBtn.tagName !== 'FDS-BUTTON') {
          throw new Error('fds-alert can only accept a fds-button in the alert-button slot');
        }

        if (alertBtn.attributes.getNamedItem('variant')?.value !== 'tertiary') {
          throw new Error('fds-alert button must have variant="tertiary"');
        }
      }
    }
  }

  private addHeadingClass(alertHeading: HTMLHeadingElement) {
    switch (alertHeading.tagName) {
      case 'H2':
      case 'H3':
      case 'H4':
      case 'H5':
      case 'H6': {
        alertHeading.classList.add('fds-alert__header');
        break;
      }
      default: {
        throw new Error('fds-alert can only accept an h2..h6 in the alert-header slot');
      }
    }
  }

  private getIconName(): IconName {
    const iconMap: { [key in FdsAlertVariant]: IconName } = {
      info: 'info_filled',
      error: 'cancel_filled',
      warning: 'error_filled',
      success: 'check_circle_filled',
    };
    return iconMap[this.variant] ?? 'info_filled';
  }

  /**
   * Toggle the visibility of the alert
   */
  @Method()
  async toggle(): Promise<void> {
    this.show = !this.show;
  }

  private handleDismissAlert = () => {
    this.show = false;
    this.fdsDismiss.emit();
    this.fdsAlertDismiss.emit();
  };

  render() {
    const { variant, show, disableDismiss, handleDismissAlert } = this;

    return (
      <Host
        role={disableDismiss ? 'note' : 'alertdialog'}
        class={`fds-alert--${variant} ${state.mode ?? ''} ${show ? '' : 'fds-alert--hidden'}`}
      >
        <header class="fds-alert__title">
          <div class="fds-alert__header-wrapper">
            <span class="fds-alert__icon">
              <fds-icon name={this.getIconName()} />
            </span>
            <slot name="alert-heading" ref={(el) => (this.alertHeading = el as HTMLSlotElement)} />
          </div>
          {disableDismiss ? undefined : (
            <span class="fds-alert__close-icon">
              <fds-icon-button variant="tertiary" icon="close" onClick={handleDismissAlert} />
            </span>
          )}
        </header>
        <div class="fds-alert__description">
          <slot />
          <slot name="alert-link" ref={(el) => (this.alertLink = el as HTMLSlotElement)} />
          <slot name="alert-button" ref={(el) => (this.alertBtn = el as HTMLSlotElement)} />
        </div>
      </Host>
    );
  }
}
